/**
 * ProgrammeSlides.jsx — Programme officiel Permis AM Voiturette en 4 sessions,
 * présenté comme un carrousel de slides "conforme référentiel".
 *
 * Chaque slide est une planche pédagogique complète (illustrations + objectifs +
 * cadre réglementaire + focus visuels). Les images sont servies depuis /public
 * pour un chargement rapide et un rendu print-friendly.
 */
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Target, ZoomIn, X } from 'lucide-react';
import { Button } from '../ui/button';

const SLIDES = [
  {
    id: 1,
    title: 'Découvrir et prendre en main',
    duree: '2h',
    color: '#1e3a8a', // bleu foncé
    accent: 'bg-blue-950',
    ring: 'ring-blue-500/40',
    src: '/programme-voiturette/session-1.png',
    resume: 'Prise de contact, sécurité routière, découverte du quadricycle et premiers exercices hors circulation.',
  },
  {
    id: 2,
    title: 'Maîtriser et commencer à circuler',
    duree: '2h',
    color: '#166534', // vert
    accent: 'bg-green-900',
    ring: 'ring-emerald-500/40',
    src: '/programme-voiturette/session-2.png',
    resume: 'Manœuvres essentielles, règles de circulation et première mise en circulation supervisée.',
  },
  {
    id: 3,
    title: 'Circuler en sécurité',
    duree: '2h',
    color: '#c2410c', // orange
    accent: 'bg-orange-900',
    ring: 'ring-orange-500/40',
    src: '/programme-voiturette/session-3.png',
    resume: 'Adaptation de l\'allure, observation, partage de la route, agglomération et hors agglomération.',
  },
  {
    id: 4,
    title: 'Vers l\'autonomie',
    duree: '2h',
    color: '#6b21a8', // violet
    accent: 'bg-purple-950',
    ring: 'ring-purple-500/40',
    src: '/programme-voiturette/session-4.png',
    resume: 'Parcours autonome, sensibilisation aux risques, évaluation finale et remise d\'attestation.',
  },
];

export default function ProgrammeSlides() {
  const [current, setCurrent] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const slide = SLIDES[current];

  const prev = () => setCurrent((c) => (c === 0 ? SLIDES.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === SLIDES.length - 1 ? 0 : c + 1));

  return (
    <section className="mt-10 mb-14" data-testid="programme-slides">
      {/* En-tête */}
      <div className="max-w-6xl mx-auto text-center mb-6 px-4">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 text-[10px] font-black uppercase tracking-[0.22em] mb-3">
          <Target className="w-3 h-3" /> Programme officiel · 4 sessions × 2h
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Le parcours complet en 4 étapes visuelles
        </h2>
        <p className="mt-2 text-sm sm:text-base text-white/60 max-w-2xl mx-auto">
          Chaque slide résume les objectifs pédagogiques, le cadre réglementaire et les
          focus visuels d'une session — un support conforme à l'arrêté du 8 novembre 2012
          modifié, prêt à projeter en salle ou à partager par QR aux élèves.
        </p>
      </div>

      {/* Slide courante */}
      <div className="max-w-6xl mx-auto px-4">
        <div
          className={`relative rounded-2xl overflow-hidden border-2 shadow-2xl bg-black/40 ring-4 ${slide.ring} transition`}
          style={{ borderColor: `${slide.color}70` }}
          data-testid={`programme-slide-${slide.id}`}
        >
          <img
            src={slide.src}
            alt={`Session ${slide.id} · ${slide.title}`}
            className="w-full h-auto object-contain block"
            loading="lazy"
          />
          {/* Bouton zoom */}
          <button
            type="button"
            onClick={() => setZoomed(true)}
            className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-black/70 hover:bg-black/85 border border-white/20 text-white text-[11px] font-black uppercase tracking-widest backdrop-blur"
            data-testid={`programme-slide-zoom-${slide.id}`}
            aria-label="Agrandir la slide"
          >
            <ZoomIn className="w-3.5 h-3.5" /> Agrandir
          </button>
        </div>

        {/* Résumé + Navigation */}
        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: slide.color }}>
              Session {slide.id} · {slide.duree}
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {slide.title}
            </h3>
            <p className="mt-1 text-[13px] sm:text-sm text-white/70 leading-snug">{slide.resume}</p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={prev}
              variant="outline"
              size="sm"
              className="border-white/20 text-white/85 hover:bg-white/5"
              data-testid="programme-slide-prev"
              aria-label="Session précédente"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-[11px] font-black uppercase tracking-widest text-white/70 tabular-nums px-2">
              {slide.id} / {SLIDES.length}
            </div>
            <Button
              onClick={next}
              size="sm"
              style={{ background: slide.color }}
              className="text-white hover:opacity-90"
              data-testid="programme-slide-next"
              aria-label="Session suivante"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Sélecteur mini-thumbs */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {SLIDES.map((s, i) => {
            const active = i === current;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setCurrent(i)}
                className={`relative rounded-xl overflow-hidden border-2 transition group ${
                  active ? 'opacity-100 scale-[1.02]' : 'opacity-65 hover:opacity-100'
                }`}
                style={{
                  borderColor: active ? s.color : 'rgba(255,255,255,0.1)',
                  boxShadow: active ? `0 0 0 3px ${s.color}44` : 'none',
                }}
                data-testid={`programme-slide-thumb-${s.id}`}
                aria-label={`Aller à la session ${s.id}`}
                aria-pressed={active}
              >
                <img src={s.src} alt="" className="w-full h-20 sm:h-24 object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-1.5 left-1.5 right-1.5 text-left">
                  <div className="inline-flex items-center gap-1 px-1.5 h-4 rounded text-white text-[9px] font-black uppercase tracking-widest" style={{ background: s.color }}>
                    <Clock className="w-2.5 h-2.5" /> S{s.id} · {s.duree}
                  </div>
                  <div className="text-white text-[10px] sm:text-[11px] font-black leading-tight mt-0.5 line-clamp-1">
                    {s.title}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Modal Zoom fullscreen */}
      {zoomed && (
        <div
          className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 cursor-zoom-out"
          onClick={() => setZoomed(false)}
          data-testid="programme-slide-zoom-modal"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 h-10 rounded-lg bg-white/10 hover:bg-white/20 border border-white/25 text-white text-[11px] font-black uppercase tracking-widest"
            data-testid="programme-slide-zoom-close"
            aria-label="Fermer le zoom"
          >
            <X className="w-4 h-4" /> Fermer
          </button>
          <img
            src={slide.src}
            alt={`Session ${slide.id} · ${slide.title}`}
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
